import {
  EnclosureFlags,
  EnclosureFlagsConstants,
  EnclosureProperties,
  IEnclosure,
  IEnclosureProperties,
  IEnclosureSpecs
} from '../../shared/models/enclosure.model';
import { IEnclosureType } from '../models/enclosure-type.model';
import { IsPrismaXsEnclosurePipe } from '../../ui/pipes/is-prisma-xs-enclosure.pipe';
import { PragmaCustomizedMountingType } from '../models/pragma-customized.model';
import { ProductRangesConstants } from '../models/product-ranges.constants';
import { generateDeviceLabel } from './product.helper';
import {
  IAddDeviceToDispositionDto, IWizardAddDeviceQuantity,
  IWizardConfigurationDevice, IWizardCustomProduct,
  IWizardCustomProductInConfiguration
} from '../models/wizard-device.model';
import { OCCUPANCY_THRESHOLD } from '../models/enclosure.constants';

export const flagExistsInEnclosures = (enclosureFlags: EnclosureFlags[], flag: EnclosureFlagsConstants) => {
  return Array.isArray(enclosureFlags) && !!enclosureFlags.find(f => f.flag === flag);
};

export const getEnclosureAvailableSelectedCurrent = (enclosure: IEnclosureType, selectedCurrent: string): string => {
  const defaultCurrent = parseInt(enclosure.defaultRatedCurrent, 10);
  if (selectedCurrent === '0A') {
    return `${ defaultCurrent }A`;
  }

  const selected = parseInt(selectedCurrent, 10);
  const maxAllowed = parseInt(enclosure.allowedRatedCurrents[enclosure.allowedRatedCurrents.length - 1], 10);
  const allowedCurrent = maxAllowed >= selected ? selected : maxAllowed;

  return `${ allowedCurrent }A`;
}

export const getEnclosureRatedCurrent = (properties: IEnclosureProperties[]): string => {
  return properties?.find(p => p.property === EnclosureProperties.RATED_CURRENT)?.value;
}

export const enclosurePropertyExistsInPropertiesArray = (enclosureProperties: IEnclosureProperties[],
                                                         enclosureProperty: EnclosureProperties): boolean => {
  return enclosureProperties.some(property => property.property === enclosureProperty);
}

export const addNewEnclosureProperty = (enclosureProperties: IEnclosureProperties[],
                                        enclosureProperty: IEnclosureProperties): IEnclosureProperties[] => {
  return [].concat(enclosureProperties, enclosureProperty);
}

export const updateExistingProperty = (enclosureProperties: IEnclosureProperties[],
                                       enclosureProperty: IEnclosureProperties): IEnclosureProperties[] => {
  return enclosureProperties.map(property => {
    if (property.property !== enclosureProperty.property) {
      return property
    }

    return {
      ...property,
      value: enclosureProperty.value
    };
  })
}

export const updateEnclosureProperty = (enclosure: IEnclosure, enclosureProperty: IEnclosureProperties): IEnclosure => {
  if (!enclosureProperty) {
    return enclosure;
  }

  // eslint-disable-next-line max-len
  const enclosureProperties = enclosurePropertyExistsInPropertiesArray(enclosure.enclosureProperties, enclosureProperty.property as EnclosureProperties) ?
    updateExistingProperty(enclosure.enclosureProperties, enclosureProperty) :
    addNewEnclosureProperty(enclosure.enclosureProperties, enclosureProperty);

  return {
    ...enclosure,
    enclosureProperties
  }
}

export const getValueOfEnclosureProperty = (enclosureProperties: IEnclosureProperties[],
                                            propertyDescription: EnclosureProperties): IEnclosureProperties => {
  return enclosureProperties?.find(property => property?.property === propertyDescription);
}

export const calculateAvailableModules = (occupiedModules: number) => {
  return 100 - (occupiedModules <= 100 ? occupiedModules : 100);
}

export const getEnclosureType = (enclosure: IEnclosure) : string => {
  if(new IsPrismaXsEnclosurePipe().transform(enclosure) && typeof(enclosure?.mountingType) === 'string') {
    return enclosure?.mountingType;
  }

  return enclosure?.enclosureDescription?.mountingType;
}

export const getPragmaCustomizedEnclosureMountingType = (mountingType: number | string): PragmaCustomizedMountingType => {
  if (typeof (mountingType) === 'string') {
    return undefined;
  }

  switch (mountingType) {
    case 0:
      return PragmaCustomizedMountingType.UP;
    case 1:
      return PragmaCustomizedMountingType.AP;
    default:
      return undefined;
  }
}

export const getDevicesAddedToWizardConfiguration =
  (deviceId: number, devicesExistingInConfiguration: IWizardConfigurationDevice[]): IWizardConfigurationDevice => {
    return  devicesExistingInConfiguration?.find(device => device?.device?.id === deviceId);
}

export const calculateTotalModulesOfDevicesInWizard = (devices: IWizardConfigurationDevice[],
                                                       customDevices: IWizardCustomProductInConfiguration[]): number => {
  const customDevicesSpace = customDevices?.reduce((total, addedDevice) => {
    return total + addedDevice?.modules;
  }, 0)

  return devices?.reduce((total, addedDevice) => {
    return total + addedDevice?.device?.modules * addedDevice.labels.length;
  }, customDevicesSpace);
}

export const enclosureTypeSupportsWizard = (enclosureType: IEnclosureType, availableProductRanges: number[]): boolean => {
  return availableProductRanges?.includes(enclosureType?.productRangeId);
}

export const getEnclosureSpecsAccordingToType = (type: PragmaCustomizedMountingType, enclosureType: IEnclosureType, availableProductRanges: number[]): IEnclosureSpecs[] => {
  if (!enclosureTypeSupportsWizard(enclosureType, availableProductRanges)) {
    return [];
  }

  if (type === PragmaCustomizedMountingType.UP) {
    return enclosureType?.enclosureSpecs?.filter(spec => spec.isUP);
  }
  if (type === PragmaCustomizedMountingType.AP) {
    return enclosureType?.enclosureSpecs?.filter(spec => spec.isAP);
  }

  return [];
}

export const isEnclosureOccupancyBelowThreshold = (devicesRequiredSpace: number, spec: IEnclosureSpecs) => {
  const availableSpace = spec.numberOfModulesPerRow * 2 * spec.numberOfRows;
  const occupancyRatio = devicesRequiredSpace / availableSpace;

  return occupancyRatio <= OCCUPANCY_THRESHOLD;
}

export const shouldReplaceDefaultEnclosure = (spec: IEnclosureSpecs, currentCandidateDefaultEnclosure: IEnclosureSpecs): boolean => {
  if (!currentCandidateDefaultEnclosure) {
    return true;
  }
  return spec.numberOfRows < currentCandidateDefaultEnclosure.numberOfRows;
};

const updateDefaultEnclosure = (availableSpecs: IEnclosureSpecs[], devicesRequiredSpace: number) => {
  let newDefaultEnclosure: IEnclosureSpecs;

  if (!availableSpecs || availableSpecs.length === 0) {
    return;
  }

  availableSpecs.forEach(spec => {

    if (isEnclosureOccupancyBelowThreshold(devicesRequiredSpace, spec) && shouldReplaceDefaultEnclosure(spec, newDefaultEnclosure)) {
      newDefaultEnclosure = spec;
    }
  });

  if (!newDefaultEnclosure) {
    newDefaultEnclosure = availableSpecs[availableSpecs.length - 1];
  }

  availableSpecs.forEach(spec => {
    spec.isDefault =
      spec.enclosureSpecId === newDefaultEnclosure.enclosureSpecId ? 1 : 0;
  });

};

export const getEnclosureSpecsAccordingToOccupiedPlacesInWizard = (enclosureSpecs: IEnclosureSpecs[],
                                                                   addedDevices: IWizardConfigurationDevice[],
                                                                   // eslint-disable-next-line max-len
                                                                   customDevices: IWizardCustomProductInConfiguration[]): IEnclosureSpecs[] => {
  if (!enclosureSpecs || enclosureSpecs.length === 0) {
    return [];
  }
  const availableSpecs: IEnclosureSpecs[] = [];

  const devicesRequiredSpace = calculateTotalModulesOfDevicesInWizard(addedDevices, customDevices);

  enclosureSpecs.forEach((spec: IEnclosureSpecs) => {
    const enclosureTypeAvailableSpace = spec?.numberOfModulesPerRow * 2 * spec?.numberOfRows;

    if (enclosureTypeAvailableSpace >= devicesRequiredSpace) {
      availableSpecs.push(spec);
    }
  });
  updateDefaultEnclosure(availableSpecs, devicesRequiredSpace);

  return availableSpecs;
}

export const generateLabelsForNewDevices = (existingDevices: IWizardConfigurationDevice[],
                                            addedProduct: IWizardAddDeviceQuantity,
                                            allLabels: string[]): string[] => {
  const labels = existingDevices?.find(device => device.device?.id === addedProduct?.device?.id)?.labels || [];
  for (let i = 0; i < addedProduct?.quantity; i++) {
    const newDeviceLabel = generateDeviceLabel(addedProduct?.device?.label, allLabels);
    labels.push(newDeviceLabel);
    allLabels.push(newDeviceLabel);
  }

  return labels;
}

export const getExistingLabelsInWizard = (addedDevices: IWizardConfigurationDevice[],
                                          customProductsLabels:  string[]): string[] => {
  const existingLabels = [];
  addedDevices?.forEach(addedDevice => {
    addedDevice.labels.forEach((label) => {
      existingLabels.push(label);
    })
  });

  customProductsLabels?.forEach(label => {
    existingLabels.push(label);
  })

  return existingLabels;
}

export const getAllLabelsInConfiguration = (addedDevices: IWizardConfigurationDevice[],
                                            customDevices: IWizardCustomProductInConfiguration[]): string[] => {
  return getExistingLabelsInWizard(addedDevices,
    customDevices.map(customDevice => customDevice.label));
}

export const modifyAddedDevicesInWizardConfiguration = (addedProduct: IWizardAddDeviceQuantity,
                                                        addedDevices: IWizardConfigurationDevice[],
                                                        // eslint-disable-next-line max-len
                                                        customDevices: IWizardCustomProductInConfiguration[]): IWizardConfigurationDevice[] => {
  if (!addedProduct) {
    return addedDevices;
  }

  const alreadyAddedProductIndex = addedDevices.findIndex(addedDevice => addedDevice?.device?.id === addedProduct?.device?.id);
  if (addedProduct?.quantity === 0) {
    addedDevices.splice(alreadyAddedProductIndex, 1);

    return addedDevices;
  }

  if (alreadyAddedProductIndex === -1) {
    addedDevices.push({
      device: addedProduct?.device,
      labels: generateLabelsForNewDevices(addedDevices, addedProduct, getAllLabelsInConfiguration(addedDevices, customDevices))
    });

    return addedDevices;
  }

  if (addedProduct?.quantity > addedDevices[alreadyAddedProductIndex]?.labels?.length) {
    for (let i = addedDevices[alreadyAddedProductIndex]?.labels?.length; i < addedProduct?.quantity; i++) {
      addedDevices[alreadyAddedProductIndex].labels.push(
        generateDeviceLabel(addedProduct?.device?.label, getAllLabelsInConfiguration(addedDevices, customDevices)));
    }
    return addedDevices;
  }

  if (addedProduct?.quantity < addedDevices[alreadyAddedProductIndex]?.labels?.length) {
    for (let i = addedDevices[alreadyAddedProductIndex]?.labels?.length; i > addedProduct?.quantity; i--) {
      addedDevices[alreadyAddedProductIndex]?.labels?.pop();
    }
    return addedDevices;
  }
}

export const mapToWizardConfigurationDto = (devices: IWizardConfigurationDevice[]): IAddDeviceToDispositionDto[] => {
  const addedDevices: IAddDeviceToDispositionDto[] = [];

  devices?.forEach(device => {
    device.labels.forEach(label => {
      addedDevices.push({
        label,
        wizardDeviceId: device?.device?.id,
        applicationGroupId: device?.device?.applicationGroupId
      })
    })
  });

  return addedDevices;
}

export const mapToCustomDeviceWizardConfigurationDto = (customDevices: IWizardCustomProductInConfiguration[])
  : IWizardCustomProduct[] => {
  return customDevices?.map(device => {
    return {
      description: device?.description,
      leakageCurrent: device?.leakageCurrent,
      poleId: device?.poleId,
      ampere: device?.ampere,
      label: device?.label
    }
  })
}
